import "./AIMessage.css";
import { AgentChatResponseToolbar } from "./AgentChatResponseToolbar";
import { useMessageThreadInfo } from "../../hooks/useMessageThreadInfo";
import { AgentFinalResponseMessage } from "./types";
import { getStoreLinkRecord } from "winslow-core";
import { useSelector } from "react-redux";
import { useState, useRef, useEffect } from "react";

import { LinkRecordActivity } from "../shares/LinkRecordActivity";
import { selectLinkRecords } from "../../redux/features/appData/appDataSlice";
import { ThreadInfo } from "./ThreadInfo";
import showdown from "showdown";
type AIMessageProps = {
  message: AgentFinalResponseMessage;
  onExpand: (message: AgentFinalResponseMessage) => void;
};

// Configure showdown converter
const converter = new showdown.Converter();
// Prevent nested lists on new lines and customize output
converter.setOption("simpleLineBreaks", true);
converter.setOption("smoothLivePreview", true);
converter.setOption("openLinksInNewWindow", true);
converter.setOption("disableForced4SpacesIndentedSublists", true);
converter.setOption("ghCompatibleHeaderId", true);
converter.setOption("splitAdjacentBlockquotes", true);

// const convertMarkdownToHtml = (markdown: string): string => {
//   if (!markdown) return "";

//   let html = markdown;

//   // Convert bold text: **text** -> <strong>text</strong>
//   html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

//   // Convert links: [text](url) -> <a href="url">text</a>
//   html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

//   // Convert headers: # Header -> <h1>Header</h1>, ## Header -> <h2>Header</h2>, etc.
//   html = html.replace(/^(#{1,6})\s+(.*?)$/gm, (_, hashes, text) => {
//     const level = hashes.length;
//     return `<h${level}>${text}</h${level}>`;
//   });

//   // Process lists - first split into lines
//   const lines = html.split("\n");
//   let inList = false;
//   let inNestedList = false;

//   for (let i = 0; i < lines.length; i++) {
//     // Any list item (ordered or unordered): 1. item or - item
//     if (/^\d+\.\s+(.+)$/.test(lines[i])) {
//       const content = lines[i].replace(/^\d+\.\s+(.+)$/, "$1");

//       if (!inList) {
//         lines[i] = "<ul>";
//         lines[i] += `<li>${content}`;
//         inList = true;
//       } else {
//         lines[i] = `<li>${content}`;
//       }

//       // Check if next line is a nested list
//       if (i + 1 < lines.length && /^\s+-\s+(.+)$/.test(lines[i + 1])) {
//         lines[i] += "<ul>";
//         inNestedList = true;
//       } else {
//         lines[i] += "</li>";
//       }
//     }
//     // Nested list: - item (indented)
//     else if (/^\s+-\s+(.+)$/.test(lines[i])) {
//       const content = lines[i].replace(/^\s+-\s+(.+)$/, "$1");

//       lines[i] = `<li>${content}</li>`;

//       // Check if next line is not a nested list
//       if (i + 1 < lines.length && !/^\s+-\s+(.+)$/.test(lines[i + 1])) {
//         lines[i] += "</ul>";
//         inNestedList = false;
//       }
//     }
//     // End of list
//     else if (inList && lines[i].trim() === "") {
//       lines[i] = "</ul>";
//       inList = false;
//     }
//   }

//   // Close any open lists
//   if (inList) {
//     lines.push("</ul>");
//   }

//   // Join lines back together
//   html = lines.join("\n");

//   // Replace multiple newlines with a single one
//   html = html.replace(/\n+/g, "\n");

//   // Add line breaks, but not between list elements
//   html = html.replace(
//     /\n(?!<\/?[uo]l>|<li>|<\/li>)(?!.*<\/?[uo]l>|<li>|<\/li>)/g,
//     "<br>"
//   );

//   // Replace any consecutive <br> tags with a single one
//   html = html.replace(/(<br>\s*)+/g, "<br>");

//   // Remove any <br> tags between list elements
//   html = html.replace(/<\/li>\s*<br>\s*<li>/g, "</li><li>");
//   html = html.replace(/<\/ul>\s*<br>\s*<ul>/g, "</ul><ul>");
//   html = html.replace(/<ul>\s*<br>/g, "<ul>");
//   html = html.replace(/<br>\s*<\/ul>/g, "</ul>");

//   return html;
// };

export const AIMessage = ({ message, onExpand }: AIMessageProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const contextInfo = useMessageThreadInfo(message.messageThread);
  const finalResponse = message.finalResponse;
  const linkRecords = useSelector(selectLinkRecords);

  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      const needsToExpand = contentHeight > 250;
      setNeedsExpansion(needsToExpand);
    }
  }, [finalResponse.answer]);

  const handleExpand = () => {
    if (needsExpansion) {
      setIsExpanded((prev) => !prev);
    }
    onExpand(message);
  };

  const handlePreviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onExpand(message);
  };

  // const getContentClass = () => {
  //   if (!needsExpansion) return "";
  //   return isExpanded ? "expanded" : "collapsed";
  // };

  return (
    <div className="agent-chat-history-prompt-response">
      <div
        className={`agent-chat-history-prompt-response-object `}
        onClick={handleExpand}>
        <div className="agent-chat-history-general">
          <div
            className="agent-chat-q-and-a-preview"
            onClick={handlePreviewClick}>
            {finalResponse.sources && finalResponse.sources.length > 0 && (
              <div className="agent-chat-q-and-a-preview-sources">
                {finalResponse.sources.map((linkRecordId) => {
                  const linkRecord = getStoreLinkRecord(
                    linkRecordId,
                    linkRecords ?? []
                  );
                  if (!linkRecord) return null;

                  return (
                    <LinkRecordActivity
                      key={linkRecordId}
                      linkRecord={linkRecord}
                      minimum={true}
                      showDrag={false}
                      clickIsOpen={true}
                    />
                  );
                })}
              </div>
            )}
            <div
              ref={contentRef}
              className="agent-chat-q-and-a-preview-content"
              dangerouslySetInnerHTML={{
                __html: converter.makeHtml(finalResponse.answer),
              }}>
              {/* Content is injected via dangerouslySetInnerHTML */}
            </div>
          </div>
        </div>
      </div>

      <div className="agent-chat-history-item-toolbar">
        <AgentChatResponseToolbar
          key={`toolbar-${message.id}`}
          message={message}
        />
        {contextInfo && (
          <ThreadInfo avatar={contextInfo.avatar} label={contextInfo.label} />
        )}
      </div>
    </div>
  );
};
